import { theme } from 'components/Theme/main';
import { mapAvailabilityData, mapStoreAvailability } from 'connectors/availability/Availability';
import { getFirstImage } from 'connectors/image/Image';
import { mapLoyaltyPointsObtainedData, mapProductPriceData, mapProductPricesData } from 'connectors/price/Prices';
import { mapSliderProductApiData } from 'connectors/products/Products';
import {
    MainVariantDetailFragmentApi,
    ProductColorVariantFragmentApi,
    ProductDetailFragmentApi,
    ProductDetailInterfaceFragmentApi,
    VariantDetailFragmentApi,
} from 'graphql/generated';
import { formatProductNameWithNonBreakingSpaces } from 'helpers/formaters/formatText';
import { mapProductRecommendationAreas } from 'hooks/productRecommendationArea/useProductRecommendationAreas';
import { isProductFileTypeType, ProductFileType } from 'types/file';
import { ImageType } from 'types/image';
import {
    ColorVariantType,
    PictogramParameterType,
    ProductDetailInterfaceType,
    ProductDetailType,
    VariantDetailType,
} from 'types/product';
import { ReviewsSummary, ReviewType } from 'types/review';
import { mapVirtualSetApiData } from 'types/virtualSet';

const mapProductDetailInterface = (
    productDetailApiData: ProductDetailInterfaceFragmentApi,
    currencyCode: string,
): ProductDetailInterfaceType => {
    return {
        ...productDetailApiData,
        __typename: productDetailApiData.__typename,
        description: productDetailApiData.description !== null ? productDetailApiData.description : '',
        name: formatProductNameWithNonBreakingSpaces(productDetailApiData.name),
        price: mapProductPriceData(productDetailApiData.price, currencyCode),
        prices: mapProductPricesData(productDetailApiData.prices, currencyCode),
        loyaltyClubPointsObtained: mapLoyaltyPointsObtainedData(productDetailApiData.loyaltyClubPointsObtained),
        accessories: mapSliderProductApiData(productDetailApiData.accessories, currencyCode),
        spareParts: mapSliderProductApiData(productDetailApiData.spareParts, currencyCode),
        availability: mapAvailabilityData(productDetailApiData.availability),
        images: mapProductImages(productDetailApiData),
        deliveryDaysFromCentralStore: productDetailApiData.deliveryDaysFromCentralStore,
        youtubeVideoId: productDetailApiData.youtubeVideoId,
        youtubeUploadDate: productDetailApiData.youtubeUploadDate,
        benefits: productDetailApiData.benefits !== null ? productDetailApiData.benefits : [],
        brand: productDetailApiData.brand,
        parameterImage: productDetailApiData.parameterImage,
        pictogramParameters: mapPictogramParameters(productDetailApiData),
        isSellingDenied: productDetailApiData.isSellingDenied,
        files: mapProductFilesTypes(productDetailApiData.filesByType),
        defaultStoreAvailability:
            productDetailApiData.defaultStoreAvailability !== null
                ? mapStoreAvailability(productDetailApiData.defaultStoreAvailability)
                : null,
        categoryNames: productDetailApiData.categories.map((category) => category.name),
        isElectronicGiftVoucher: productDetailApiData.isElectronicGiftVoucher,
        alternateUrls: productDetailApiData.alternateUrls ?? [],
        centralStockQuantity: productDetailApiData.centralStockQuantity,
        isVoucher: productDetailApiData.isVoucher,
        referencePrice:
            productDetailApiData.referencePrice !== null
                ? Number.parseFloat(productDetailApiData.referencePrice)
                : null,
        reviews: productDetailApiData.reviews as ReviewType[],
        reviewsSummary: productDetailApiData.reviewsSummary as ReviewsSummary,
        pricePerUnit: productDetailApiData.pricePerUnit
            ? {
                  unit: productDetailApiData.pricePerUnit.unit,
                  price: Number.parseFloat(productDetailApiData.pricePerUnit.price),
              }
            : null,
        maximumOrderableQuantity: Number(productDetailApiData.maximumOrderableQuantity),
        producerInfo: productDetailApiData.producerInfo,
        faq: productDetailApiData.faq,
        recommendationAreas: productDetailApiData.productDetailRecommendationAreas
            ? mapProductRecommendationAreas(productDetailApiData.productDetailRecommendationAreas, currencyCode)
            : [],
        virtualSet: productDetailApiData.virtualSet
            ? mapVirtualSetApiData(productDetailApiData.virtualSet, productDetailApiData.uuid, currencyCode)
            : null,
    };
};

export const mapProductDetailApiData = (
    productDetailApiData: ProductDetailFragmentApi,
    currencyCode: string,
): ProductDetailType => {
    return {
        ...mapProductDetailInterface(productDetailApiData, currencyCode),
        __typename: productDetailApiData.__typename,
    };
};

export const mapVariantDetailApiData = (
    apiData: MainVariantDetailFragmentApi | VariantDetailFragmentApi,
    currencyCode: string,
): VariantDetailType => {
    return {
        ...mapProductDetailInterface(apiData, currencyCode),
        __typename: apiData.__typename,
        colorVariants: apiData.colorVariants.map((variant) => mapColorVariant(variant)),
        currentColorVariant: 'currentColorVariant' in apiData ? mapColorVariant(apiData.currentColorVariant) : null,
    };
};

const mapColorVariant = (variant: ProductColorVariantFragmentApi): ColorVariantType => ({
    ...variant,
    value: variant.value ?? theme.color.gray,
    parameterValueImages: variant.parameterValueImages ?? null,
});

export const mapPictogramParameters = (apiData: ProductDetailInterfaceFragmentApi): PictogramParameterType[] | null => {
    const mappedData = [];

    for (const parameter of apiData.pictogramParameters) {
        if (parameter !== null && parameter.isPictogramType) {
            mappedData.push({
                uuid: parameter.uuid,
                name: parameter.name,
                slug: parameter.slug,
                image: getFirstImage(parameter.images),
                isWarrantyType: parameter.isWarrantyType,
            });
        }
    }

    if (mappedData.length !== 0) {
        return mappedData;
    }

    return null;
};

export const mapProductFilesTypes = (apiData: ProductDetailInterfaceFragmentApi['filesByType']): ProductFileType[] => {
    const mappedData = [];

    for (const file of apiData) {
        mappedData.push({
            ...file,
            type: isProductFileTypeType(file.type) ? file.type : null,
        });
    }

    return mappedData;
};

export const mapProductImages = (apiData: ProductDetailInterfaceFragmentApi): ImageType[] => {
    const mappedData = [];

    for (const image of apiData.images) {
        mappedData.push({
            __typename: 'Image',
            name: apiData.name,
            url: image,
        });
    }

    return mappedData as ImageType[];
};
